// EmailSidebarSection — live folder tree rendered INSIDE the global
// Stewardship sidebar's EMAIL section.
//
// Per Grant's UX direction (2026-05-01): email folders should live in
// the same left-rail as CEO / COMPANIES / ADMIN sections, not in a
// separate Outlook-style folder pane. Clicking a folder navigates to
// /email/folders/<id>, which renders the message list + reading pane
// in the main content area (no full-page chrome swap).
//
// Layout:
//   * One static Search shortcut at top (deep-links to /email/search)
//   * Recursive folder tree (live from /api/email/folders) with:
//       - Microsoft well-known folders ordered Inbox → Drafts →
//         Sent → Deleted → Junk → Outbox → Conversation History
//       - Custom folders alphabetical
//       - Per-folder unread count badge
//       - Expand/collapse arrows on folders that have children
//
// Visual style mirrors the surrounding sidebar so it feels native:
//   * `.sidebar-link` class for each folder row (same hover/active
//     treatment as other sidebar items)
//   * `.sidebar-link-icon` slot rendered with a per-folder SVG via
//     resolveFolderIconName()

import { Star } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import {
  api,
  ApiError,
  type CreateSearchFolderInput,
  type EmailMailFolder,
  type SearchFolderSummary,
} from "../../api";
import { isNotConnectedError } from "../NotConnectedNotice";
import { useEmailFavorites } from "../../utils/email-favorites";
import { getFolderExpandState, setFolderExpanded } from "../../utils/email-folder-expand-state";
import { applyPrefsToFolderIds, useFolderPrefs } from "../../utils/email-folder-prefs";
import {
  EMAIL_DESTINATION_ITEMS,
  sidebarItemIsActive,
  type IconName,
  type SidebarItem,
} from "../sidebar-data";
import { useToast } from "../Toast";
import { CreateSearchFolderDialog } from "./CreateSearchFolderDialog";
import { isOwaAutoGeneratedName } from "./email-sidebar-filters";
import { EmailComposer } from "./EmailComposer";
import { FolderPrefsDialog } from "./FolderPrefsDialog";
import { MailboxCategories } from "./MailboxCategories";
import { MailboxSwitcher } from "./MailboxSwitcher";
import { describeEmailFolderFailure } from "./email-folder-error";
import { useSelectedMailbox } from "../../utils/email-selected-mailbox";
import { useAutoRefresh } from "../../utils/use-auto-refresh";
import { isEmailDrag, readEmailDragPayload } from "../../utils/email-move";
import { useMoveEmail } from "./useMoveEmail";

const SYSTEM_ORDER: Record<string, number> = {
  inbox: 1,
  drafts: 2,
  sentitems: 3,
  deleteditems: 4,
  archive: 5,
  junkemail: 6,
  outbox: 7,
  conversationhistory: 8,
};

// Per-folder icon name. Maps to entries in the existing SidebarIcon
// switch so the folder rows visually match other sidebar items.
//
// Microsoft Graph's /me/mailFolders response intermittently omits the
// wellKnownName field — Grant's mailbox returned wellKnownName=null on
// every system folder when we tested with the bare $expand=childFolders
// query (see graph-client-folders.ts comment block dated 2026-05-01).
// Adding `?$select=...,wellKnownName,...` triggered a 4xx on the
// childFolders inner expand and broke the entire folder pane. So in
// practice we cannot rely on wellKnownName to drive the icon picker.
//
// Fallback: when wellKnownName is null, match the displayName against
// the standard English Outlook system-folder names. Stewardship's
// advisors all run English Office 365, so "Inbox" / "Drafts" / "Sent
// Items" / "Deleted Items" / "Archive" / "Junk Email" / "Outbox" /
// "Conversation History" are stable. If a non-English mailbox shows up
// in v1.x we'll add the locale variant; for now this keeps the icons
// visually distinct on every Stewardship advisor's mailbox today.
const SYSTEM_NAME_TO_ICON: Record<string, IconName> = {
  inbox: "mail",
  drafts: "draft",
  "sent items": "send",
  "deleted items": "trash",
  archive: "archive",
  "junk email": "junk",
  outbox: "outbox",
  "conversation history": "chat",
};
function resolveFolderIconName(folder: EmailMailFolder): IconName {
  switch (folder.wellKnownName) {
    case "inbox":
      return "mail";
    case "drafts":
      return "draft";
    case "sentitems":
      return "send";
    case "deleteditems":
      return "trash";
    case "archive":
      return "archive";
    case "junkemail":
      return "junk";
    case "outbox":
      return "outbox";
    case "conversationhistory":
      return "chat";
    default: {
      // wellKnownName missing — try displayName fallback.
      const fromName = SYSTEM_NAME_TO_ICON[folder.displayName.toLowerCase()];
      return fromName ?? "folder";
    }
  }
}

// displayName-based fallback so the system folders still rank at the
// top of the list even when wellKnownName is null (see comment on
// SYSTEM_NAME_TO_ICON above for the rationale). Mirrors the keys used
// by SYSTEM_ORDER but indexed by displayName.toLowerCase().
const SYSTEM_NAME_TO_ORDER: Record<string, number> = {
  inbox: 1,
  drafts: 2,
  "sent items": 3,
  "deleted items": 4,
  archive: 5,
  "junk email": 6,
  outbox: 7,
  "conversation history": 8,
};
function folderSortKey(folder: EmailMailFolder): [number, string] {
  const wellKnown =
    folder.wellKnownName && folder.wellKnownName in SYSTEM_ORDER
      ? SYSTEM_ORDER[folder.wellKnownName]!
      : null;
  const fromName = SYSTEM_NAME_TO_ORDER[folder.displayName.toLowerCase()];
  const sysOrder = wellKnown ?? fromName ?? 99;
  return [sysOrder, folder.displayName.toLowerCase()];
}

function sortFolders(folders: EmailMailFolder[]): EmailMailFolder[] {
  return [...folders].sort((a, b) => {
    const [aOrd, aName] = folderSortKey(a);
    const [bOrd, bName] = folderSortKey(b);
    if (aOrd !== bOrd) return aOrd - bOrd;
    return aName.localeCompare(bName);
  });
}

function folderUrlKey(folder: EmailMailFolder): string {
  return folder.wellKnownName ?? folder.id;
}

interface EmailSidebarSectionProps {
  // The id passed by Sidebar.tsx so the heading's aria-controls
  // points at this list (and the existing
  // '.sidebar-section.is-closed > .sidebar-list { display: none }'
  // CSS rule matches when the section is collapsed).
  id: string;
  collapsed: boolean;
}

export function EmailSidebarSection({ id, collapsed }: EmailSidebarSectionProps) {
  const toast = useToast();
  const { selectedMailbox } = useSelectedMailbox();
  const [folders, setFolders] = useState<EmailMailFolder[] | null>(null);
  const [searchFolders, setSearchFolders] = useState<SearchFolderSummary[]>([]);
  const [error, setError] = useState<string | null>(null);
  // 412 "not connected" is a normal first-run STATE, not a failure —
  // render a quiet connect link instead of the red retry row
  // (2026-06-09 audit, finding 2.1).
  const [notConnected, setNotConnected] = useState(false);
  // True when the failure reason says reconnecting Office 365 is the likely fix.
  const [suggestReconnect, setSuggestReconnect] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  // Favorites + folder prefs follow the ACCOUNT (2026-08-15), so they're
  // the same on every browser this advisor signs into. No local state:
  // the settings provider holds them and re-renders on change.
  const { favorites, toggle: toggleFavorite } = useEmailFavorites();
  const { prefs: folderPrefs } = useFolderPrefs();
  const [prefsDialogOpen, setPrefsDialogOpen] = useState(false);
  const [searchFolderDialogOpen, setSearchFolderDialogOpen] = useState(false);
  const [searchFolderBusy, setSearchFolderBusy] = useState(false);
  // Persisted expand/collapse state, lifted out of each folder node so
  // it survives the folder tree re-mounting (e.g. `refresh()` briefly
  // nulling `folders` out) as well as a full page reload. See
  // email-folder-expand-state.ts for why this needed its own store.
  const [expandState, setExpandState] = useState<Record<string, boolean>>(() =>
    getFolderExpandState(),
  );
  // Lazily-fetched subfolders, keyed by parent folder.id. The root
  // /api/email/folders call only expands one level of nesting, so a
  // folder nested two-plus levels deep (an advisor's per-client
  // folders inside a custom top-level folder, e.g. Joey Ellis's
  // "Mortgage" folder) arrives with childFolderCount > 0 but an
  // empty `children` array. The first time the advisor expands such
  // a folder we fetch its children via getEmailFolderChildren() and
  // cache the result here for the rest of the session.
  const [lazyChildren, setLazyChildren] = useState<Record<string, EmailMailFolder[]>>({});
  const [lazyLoading, setLazyLoading] = useState<Record<string, boolean>>({});
  const [lazyErrors, setLazyErrors] = useState<Record<string, string>>({});

  const handleToggleFavorite = useCallback(
    (favoriteId: string) => {
      toggleFavorite(favoriteId);
    },
    [toggleFavorite],
  );

  const handleToggleExpand = useCallback((folderKey: string, expanded: boolean) => {
    setExpandState(setFolderExpanded(folderKey, expanded));
  }, []);

  // Guards against firing a second request for a folder that's
  // already loading — separate from lazyLoading state so the check
  // is synchronous (state updates aren't visible until the next
  // render, and the user can toggle-collapse-toggle fast).
  const lazyFetchInFlight = useRef<Set<string>>(new Set());

  // Fetch one folder's subfolders on demand. No-ops if already
  // cached or already loading — the expand toggle calls this on
  // every expand, not just the first.
  const fetchChildren = useCallback(
    (folderId: string) => {
      if (lazyChildren[folderId] !== undefined) return;
      if (lazyFetchInFlight.current.has(folderId)) return;
      lazyFetchInFlight.current.add(folderId);
      setLazyLoading((prev) => ({ ...prev, [folderId]: true }));
      setLazyErrors((prev) => {
        if (!(folderId in prev)) return prev;
        const next = { ...prev };
        delete next[folderId];
        return next;
      });
      void (async () => {
        try {
          const resp = await api.getEmailFolderChildren(
            folderId,
            selectedMailbox ? { mailboxUpn: selectedMailbox } : {},
          );
          setLazyChildren((prev) => ({ ...prev, [folderId]: resp.folders }));
        } catch (err) {
          const message = err instanceof ApiError ? err.message : "Couldn't load subfolders.";
          setLazyErrors((prev) => ({ ...prev, [folderId]: message }));
        } finally {
          lazyFetchInFlight.current.delete(folderId);
          setLazyLoading((prev) => {
            const next = { ...prev };
            delete next[folderId];
            return next;
          });
        }
      })();
    },
    [lazyChildren, selectedMailbox],
  );

  const refresh = useCallback(async () => {
    setError(null);
    setSuggestReconnect(false);
    setNotConnected(false);
    setFolders(null);
    // A full refresh (mailbox switch, initial load) invalidates any
    // lazily-fetched subfolders — folder ids aren't guaranteed stable
    // across mailboxes, and even within one mailbox we'd rather
    // re-fetch than show stale subfolder unread counts indefinitely.
    setLazyChildren({});
    setLazyLoading({});
    setLazyErrors({});
    try {
      const resp = await api.getEmailFolders(
        selectedMailbox ? { mailboxUpn: selectedMailbox } : {},
      );
      setFolders(resp.folders);
    } catch (err) {
      if (isNotConnectedError(err)) {
        setNotConnected(true);
        return;
      }
      const failure =
        err instanceof ApiError
          ? describeEmailFolderFailure(err.code, err.status)
          : describeEmailFolderFailure(null, null);
      setError(failure.message);
      setSuggestReconnect(failure.suggestReconnect);
    }
  }, [selectedMailbox]);

  // Silent refresh — pulls fresh folder data without resetting
  // state to null first, so the sidebar doesn't flicker between
  // loading + loaded states on every poll. Used by the
  // auto-refresh hook below. On error the existing data stays in
  // place (the user may not even notice a transient Graph hiccup).
  const silentRefresh = useCallback(async () => {
    try {
      const resp = await api.getEmailFolders(
        selectedMailbox ? { mailboxUpn: selectedMailbox } : {},
      );
      setFolders(resp.folders);
      setError(null);
    } catch {
      // Swallow — keep showing the previous folder state. The
      // user's next manual click on Inbox / refresh will surface
      // the real error if it persists.
    }
  }, [selectedMailbox]);

  // v1.x phase 11.17 polish round 4 (2026-05-04): auto-refresh
  // folder unread counts every 60s. Sidebar feels snappier than
  // the message list (60s vs 90s) because the counts are cheap to
  // refresh and updating them gives the user fast feedback that
  // new mail has arrived even when they're parked on a non-email
  // page elsewhere in the OS.
  useAutoRefresh(silentRefresh, { intervalMs: 60_000 });

  // Search folders are listed independently of the main folder tree
  // (Microsoft puts them under /me/mailFolders/searchfolders, NOT
  // /me/mailFolders, so the existing folder tree never includes
  // them). A failure here is non-fatal — the rest of the sidebar
  // still works.
  const refreshSearchFolders = useCallback(async () => {
    try {
      const resp = await api.listSearchFolders();
      // v1.x phase 11.17 polish round 2: filter out Outlook-Web's
      // auto-generated search folders (`OwaFV15.1AllItems` etc.).
      // Outlook Web Access creates these silently every time the
      // user runs a search — they accumulate as opaque
      // "OwaFV15.1Al…" names that mean nothing to the advisor.
      // Real user-created Stewardship search folders have
      // human-typed names; we surface only those.
      setSearchFolders(resp.folders.filter((f) => !isOwaAutoGeneratedName(f.displayName)));
    } catch {
      // Swallow — empty list is the same UX, and the user gets a
      // toast when explicit creation fails.
    }
  }, []);

  useEffect(() => {
    void refresh();
    void refreshSearchFolders();
  }, [refresh, refreshSearchFolders]);

  const handleCreateSearchFolder = useCallback(
    async (input: CreateSearchFolderInput) => {
      setSearchFolderBusy(true);
      try {
        const resp = await api.createSearchFolder(input);
        setSearchFolders((prev) => [...prev, resp.folder]);
        toast.success(`Created “${resp.folder.displayName}”.`);
        setSearchFolderDialogOpen(false);
      } catch (err) {
        const message =
          err instanceof ApiError ? err.message : "Couldn't create that search folder.";
        toast.error(message);
      } finally {
        setSearchFolderBusy(false);
      }
    },
    [toast],
  );

  const handleDeleteSearchFolder = useCallback(
    async (folder: SearchFolderSummary) => {
      // Cheap confirmation guard. v1 doesn't ship a confirmation
      // modal for this — the action is idempotent and reversible
      // (re-create the folder with the same name + filter), so
      // window.confirm is acceptable. If anyone ever creates a
      // search folder with months of curation we'd revisit.
      const ok = (globalThis as unknown as { confirm?: (m: string) => boolean }).confirm?.(
        `Delete the search folder “${folder.displayName}”? This won't delete any messages.`,
      );
      if (!ok) return;
      try {
        await api.deleteSearchFolder(folder.id);
        setSearchFolders((prev) => prev.filter((f) => f.id !== folder.id));
        toast.success(`Removed “${folder.displayName}”.`);
      } catch (err) {
        const message =
          err instanceof ApiError ? err.message : "Couldn't delete that search folder.";
        toast.error(message);
      }
    },
    [toast],
  );

  const sortedRoots = useMemo(() => (folders ? sortFolders(folders) : []), [folders]);

  // Apply folder prefs (hide + reorder) on top of the natural
  // system-folders-first sort. Only operates on the top-level
  // folders — sub-folders use the natural sort within their parent.
  const visibleRoots = useMemo(() => {
    if (sortedRoots.length === 0) return sortedRoots;
    const naturalIds = sortedRoots.map((f) => f.wellKnownName ?? f.id);
    const orderedIds = applyPrefsToFolderIds(naturalIds, folderPrefs);
    const byId = new Map(sortedRoots.map((f) => [f.wellKnownName ?? f.id, f]));
    return orderedIds
      .map((id) => byId.get(id))
      .filter((f): f is EmailMailFolder => f !== undefined);
  }, [sortedRoots, folderPrefs]);

  // Recursively flatten the folder tree into a single map keyed on
  // both wellKnownName AND folder.id, so favorites resolve regardless
  // of which key the user starred. Lets the FAVORITES section
  // re-render with live unread counts as the underlying folder data
  // refreshes.
  const folderLookup = useMemo(() => {
    const out = new Map<string, EmailMailFolder>();
    if (!folders) return out;
    const walk = (list: EmailMailFolder[]) => {
      for (const f of list) {
        if (f.wellKnownName) out.set(f.wellKnownName, f);
        out.set(f.id, f);
        // Fall back to lazily-fetched children when the root fetch
        // didn't reach this deep (see lazyChildren above) — otherwise
        // a favorited folder nested two-plus levels deep only
        // resolves here after the advisor has expanded its parent at
        // least once this session.
        walk(f.children.length > 0 ? f.children : (lazyChildren[f.id] ?? []));
      }
    };
    walk(folders);
    return out;
  }, [folders, lazyChildren]);

  const favoriteFolders = useMemo(() => {
    const out: EmailMailFolder[] = [];
    for (const id of favorites) {
      const folder = folderLookup.get(id);
      if (folder) out.push(folder);
    }
    return out;
  }, [favorites, folderLookup]);

  return (
    <ul className="sidebar-list" id={id}>
      {/* v1.x phase 11.7: mailbox switcher pill at the very top of
          the EMAIL section. Lets the advisor pivot the entire
          email surface (folder list + reading pane) between their
          own mailbox and the registered shared mailboxes. */}
      <li className="sidebar-mailbox-switcher-li">
        <MailboxSwitcher collapsed={collapsed} />
      </li>
      {/* New Email — primary action. Opens the EmailComposer drawer
          with no record context (backend derives household/contact
          from recipients via Phase 3 resolver). */}
      <li>
        <button
          type="button"
          className={`sidebar-link sidebar-new-email${
            collapsed ? " sidebar-new-email-collapsed" : ""
          }`}
          onClick={() => setComposeOpen(true)}
          title={collapsed ? "New email" : undefined}
        >
          <span className="sidebar-link-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" {...iconProps}>
              <path d="M12 5v14M5 12h14" strokeWidth="2" />
            </svg>
          </span>
          {!collapsed ? <span className="sidebar-link-label">New email</span> : null}
        </button>
        <EmailComposer
          open={composeOpen}
          onClose={() => setComposeOpen(false)}
          onSent={() => setComposeOpen(false)}
        />
      </li>
      {/* Static Search shortcut — visible whether folders loaded yet
          or not. Deep-links to /email/search. */}
      <li>
        <SidebarSearchLink collapsed={collapsed} />
      </li>
      {/* Non-folder email destinations (Batch Email to Clients; the
          marketing blast moved to Marketing on 2026-10-15). Rendered HERE, above the mailbox
          folder tree, because they are app surfaces rather than
          folders — and because the tree below can run long enough to
          scroll them off the screen entirely. They live in
          sidebar-data.ts so the section's item list and what actually
          paints stay one and the same thing; this component owns the
          EMAIL body, so nothing renders unless this component renders
          it (see the DECISION note on EMAIL_DESTINATION_ITEMS). Shown
          whether or not the folder fetch succeeded. */}
      {EMAIL_DESTINATION_ITEMS.map((item) => (
        <li key={item.to}>
          <EmailDestinationLink item={item} collapsed={collapsed} />
        </li>
      ))}
      {/* Loading / error states (only shown while folders === null). */}
      {!folders && !error && !notConnected ? (
        <li className="sidebar-link sidebar-link-muted" aria-busy="true">
          {!collapsed ? "Loading folders…" : null}
        </li>
      ) : null}
      {notConnected ? (
        <li
          className="sidebar-link sidebar-link-muted"
          title="Connect your Office 365 account to use email here."
        >
          {!collapsed ? (
            <Link to="/preferences" className="sidebar-connect-link">
              Connect Office 365 →
            </Link>
          ) : null}
        </li>
      ) : null}
      {error ? (
        <li className="sidebar-link sidebar-link-error" title={error}>
          {!collapsed ? (
            <>
              <span role="alert">{error}</span>
              <button type="button" onClick={() => void refresh()} className="sidebar-link-retry">
                Try again
              </button>
              {suggestReconnect ? (
                <Link to="/preferences" className="sidebar-connect-link">
                  Reconnect Office 365 →
                </Link>
              ) : null}
            </>
          ) : null}
        </li>
      ) : null}
      {/* FAVORITES — pinned shortcuts, rendered before the recursive
          tree. Hidden when there are no starred folders. The same
          favorite identifier (well-known name preferred, folder id
          fallback) is used to look up the live folder data so unread
          counts stay in sync. */}
      {favoriteFolders.length > 0 && !collapsed ? (
        <li className="sidebar-folder-favorites">
          <header className="sidebar-folder-favorites-heading">FAVORITES</header>
          <ul className="sidebar-list">
            {favoriteFolders.map((folder) => (
              <li key={`fav-${folder.id}`}>
                <FolderRow
                  folder={folder}
                  depth={0}
                  collapsed={collapsed}
                  hasChildren={false}
                  expanded={false}
                  onToggle={() => undefined}
                  favorites={favorites}
                  onToggleFavorite={handleToggleFavorite}
                />
              </li>
            ))}
          </ul>
        </li>
      ) : null}
      {visibleRoots.map((folder) => (
        <FolderNodeLi
          key={folder.id}
          folder={folder}
          depth={0}
          collapsed={collapsed}
          favorites={favorites}
          onToggleFavorite={handleToggleFavorite}
          expandState={expandState}
          onToggleExpand={handleToggleExpand}
          lazyChildren={lazyChildren}
          lazyLoading={lazyLoading}
          lazyErrors={lazyErrors}
          onFetchChildren={fetchChildren}
        />
      ))}
      {/* SEARCH FOLDERS — saved searches that act like folders.
          Created from the dialog opened by the "+ New search folder"
          row at the bottom of the section. Hidden in collapsed mode
          since there's no horizontal room for the heading. */}
      {!collapsed ? (
        <li className="sidebar-folder-search">
          <header className="sidebar-folder-favorites-heading">SEARCH FOLDERS</header>
          <ul className="sidebar-list">
            {searchFolders.map((folder) => (
              <li key={`search-${folder.id}`}>
                <SearchFolderRow
                  folder={folder}
                  onDelete={() => void handleDeleteSearchFolder(folder)}
                />
              </li>
            ))}
            <li>
              <button
                type="button"
                className="sidebar-link sidebar-search-folder-trigger"
                onClick={() => setSearchFolderDialogOpen(true)}
              >
                <span className="sidebar-link-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" {...iconProps}>
                    <path d="M12 5v14M5 12h14" strokeWidth="2" />
                  </svg>
                </span>
                <span className="sidebar-link-label">New search folder</span>
              </button>
            </li>
          </ul>
        </li>
      ) : null}
      {!collapsed ? <MailboxCategories /> : null}
      <CreateSearchFolderDialog
        open={searchFolderDialogOpen}
        onClose={() => setSearchFolderDialogOpen(false)}
        onConfirm={(input) => void handleCreateSearchFolder(input)}
        busy={searchFolderBusy}
      />
      {/* Customize folders — opens the FolderPrefsDialog. Hidden
          when the sidebar is collapsed (no horizontal room for the
          full label). */}
      {folders && !collapsed ? (
        <li>
          <button
            type="button"
            className="sidebar-link sidebar-folder-prefs-trigger"
            onClick={() => setPrefsDialogOpen(true)}
            title="Customize folders"
          >
            <span className="sidebar-link-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" {...iconProps}>
                <circle cx="12" cy="12" r="3" />
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
              </svg>
            </span>
            <span className="sidebar-link-label">Customize folders</span>
          </button>
        </li>
      ) : null}
      {folders ? (
        <FolderPrefsDialog
          open={prefsDialogOpen}
          onClose={() => setPrefsDialogOpen(false)}
          folders={sortedRoots}
        />
      ) : null}
    </ul>
  );
}

function SidebarSearchLink({ collapsed }: { collapsed: boolean }) {
  const location = useLocation();
  const isActive = sidebarItemIsActive(location.pathname, "/email/search");
  return (
    <Link
      to="/email/search"
      className={`sidebar-link${isActive ? " is-active" : ""}`}
      title={collapsed ? "Search email" : undefined}
    >
      <span className="sidebar-link-icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" {...iconProps}>
          <circle cx="11" cy="11" r="7" />
          <path d="M21 21l-4.35-4.35" />
        </svg>
      </span>
      {!collapsed ? <span className="sidebar-link-label">Search email</span> : null}
    </Link>
  );
}

// One non-folder email destination row (Batch Email to Clients).
//
// Draws its own envelope icon rather than reusing Sidebar.tsx's
// SidebarIcon: Sidebar.tsx imports THIS file, so importing back would be
// a cycle. Every item in EMAIL_DESTINATION_ITEMS is a mail surface, so
// one shared envelope is honest here; give this a real icon switch if an
// item ever needs something else.
function EmailDestinationLink({ item, collapsed }: { item: SidebarItem; collapsed: boolean }) {
  const location = useLocation();
  const isActive = sidebarItemIsActive(location.pathname, item.to);
  return (
    <Link
      to={item.to}
      className={`sidebar-link${isActive ? " is-active" : ""}`}
      title={collapsed ? item.label : undefined}
    >
      <span className="sidebar-link-icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" {...iconProps}>
          <rect x="3" y="5" width="18" height="14" rx="2" />
          <path d="M3 7l9 6 9-6" />
        </svg>
      </span>
      {!collapsed ? <span className="sidebar-link-label">{item.label}</span> : null}
    </Link>
  );
}

interface FolderNodeLiProps {
  folder: EmailMailFolder;
  depth: number;
  collapsed: boolean;
  favorites: Set<string>;
  onToggleFavorite: (id: string) => void;
  expandState: Record<string, boolean>;
  onToggleExpand: (folderKey: string, expanded: boolean) => void;
  // Lazily-fetched subfolders keyed by parent folder.id — see the
  // comment on EmailSidebarSection's lazyChildren state.
  lazyChildren: Record<string, EmailMailFolder[]>;
  lazyLoading: Record<string, boolean>;
  lazyErrors: Record<string, string>;
  onFetchChildren: (folderId: string) => void;
}

function FolderNodeLi({
  folder,
  depth,
  collapsed,
  favorites,
  onToggleFavorite,
  expandState,
  onToggleExpand,
  lazyChildren,
  lazyLoading,
  lazyErrors,
  onFetchChildren,
}: FolderNodeLiProps) {
  // Prefer children carried on the folder itself (populated one level
  // deep by the root fetch). If that's empty but Microsoft told us
  // childFolderCount > 0, fall back to whatever we've lazily fetched
  // for this folder id, if anything yet.
  const fetchedLazily = lazyChildren[folder.id];
  const rawChildren = folder.children.length > 0 ? folder.children : (fetchedLazily ?? []);
  const sortedChildren = sortFolders(rawChildren);
  // True when Microsoft says this folder has children we haven't
  // fetched yet — the case that needs an on-demand /children call
  // (e.g. Joey Ellis's per-client folders inside "Mortgage").
  const needsLazyFetch =
    folder.children.length === 0 && folder.childFolderCount > 0 && fetchedLazily === undefined;
  const hasChildren = sortedChildren.length > 0 || needsLazyFetch;
  const isLoadingChildren = lazyLoading[folder.id] === true;
  const childrenError = lazyErrors[folder.id];
  const folderKey = folder.wellKnownName ?? folder.id;
  // Top-level system folders default to expanded (Inbox shows its
  // sub-folders by default); custom folders start collapsed. Once the
  // user has explicitly toggled a folder, that choice (persisted in
  // expandState) wins over the default from then on.
  const startExpanded = depth === 0 && folder.wellKnownName !== null && hasChildren;
  const expanded = expandState[folderKey] ?? startExpanded;

  const handleToggle = () => {
    const next = !expanded;
    onToggleExpand(folderKey, next);
    if (next && needsLazyFetch) {
      onFetchChildren(folder.id);
    }
  };

  return (
    <li>
      <FolderRow
        folder={folder}
        depth={depth}
        collapsed={collapsed}
        hasChildren={hasChildren}
        expanded={expanded}
        onToggle={handleToggle}
        favorites={favorites}
        onToggleFavorite={onToggleFavorite}
      />
      {expanded && hasChildren ? (
        <ul className="sidebar-list sidebar-folder-children">
          {sortedChildren.map((child) => (
            <FolderNodeLi
              key={child.id}
              folder={child}
              depth={depth + 1}
              collapsed={collapsed}
              favorites={favorites}
              onToggleFavorite={onToggleFavorite}
              expandState={expandState}
              onToggleExpand={onToggleExpand}
              lazyChildren={lazyChildren}
              lazyLoading={lazyLoading}
              lazyErrors={lazyErrors}
              onFetchChildren={onFetchChildren}
            />
          ))}
          {sortedChildren.length === 0 && isLoadingChildren && !collapsed ? (
            <li className="sidebar-link sidebar-link-muted" aria-busy="true">
              Loading subfolders…
            </li>
          ) : null}
          {sortedChildren.length === 0 && childrenError && !collapsed ? (
            <li className="sidebar-link sidebar-link-error" title={childrenError}>
              <button
                type="button"
                onClick={() => onFetchChildren(folder.id)}
                className="sidebar-link-retry"
              >
                Subfolders failed — retry
              </button>
            </li>
          ) : null}
        </ul>
      ) : null}
    </li>
  );
}

interface FolderRowProps {
  folder: EmailMailFolder;
  depth: number;
  collapsed: boolean;
  hasChildren: boolean;
  expanded: boolean;
  onToggle: () => void;
  favorites: Set<string>;
  onToggleFavorite: (id: string) => void;
}

function FolderRow({
  folder,
  depth,
  collapsed,
  hasChildren,
  expanded,
  onToggle,
  favorites,
  onToggleFavorite,
}: FolderRowProps) {
  const location = useLocation();
  const urlKey = folderUrlKey(folder);
  const to = `/email/folders/${urlKey}`;
  const isActive = sidebarItemIsActive(location.pathname, to);
  const iconName = resolveFolderIconName(folder);
  const indent = collapsed ? 0 : depth * 12;
  // Drop target for dragging an email from the message list onto this
  // folder. Off while a shared mailbox is selected: moving there isn't
  // supported yet (own mailbox only), so we don't pretend to accept it.
  const { selectedMailbox } = useSelectedMailbox();
  const moveEmail = useMoveEmail();
  const [dropHover, setDropHover] = useState(false);
  const acceptsDrops = !selectedMailbox;
  return (
    <div
      className={`sidebar-link sidebar-folder-row${isActive ? " is-active" : ""}${dropHover ? " is-drop-target" : ""}`}
      style={indent > 0 ? { paddingLeft: `calc(12px + ${indent}px)` } : undefined}
      onDragOver={(e) => {
        if (!acceptsDrops || !isEmailDrag(e.dataTransfer)) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        if (!dropHover) setDropHover(true);
      }}
      onDragLeave={(e) => {
        // Ignore leave events fired when crossing onto a child element.
        if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
        setDropHover(false);
      }}
      onDrop={(e) => {
        setDropHover(false);
        if (!acceptsDrops) return;
        const payload = readEmailDragPayload(e.dataTransfer);
        if (!payload) return;
        e.preventDefault();
        // Dropping on the folder it already sits in is a no-op.
        if (payload.sourceFolderKey === urlKey) return;
        void moveEmail({
          messageId: payload.messageId,
          destinationFolderId: folder.id,
          destinationName: folder.displayName,
          sourceFolderKey: payload.sourceFolderKey,
        });
      }}
    >
      {hasChildren && !collapsed ? (
        <button
          type="button"
          className={`sidebar-folder-row-toggle${expanded ? "" : " is-closed"}`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onToggle();
          }}
          aria-expanded={expanded}
          aria-label={expanded ? "Collapse folder" : "Expand folder"}
        >
          <FolderChevronIcon />
        </button>
      ) : !collapsed ? (
        <span className="sidebar-folder-row-toggle-spacer" aria-hidden />
      ) : null}
      <Link
        to={to}
        className="sidebar-folder-row-link"
        title={collapsed ? folder.displayName : undefined}
      >
        <span className="sidebar-link-icon" aria-hidden="true">
          <FolderIcon name={iconName} />
        </span>
        {!collapsed ? (
          <>
            <span className="sidebar-link-label">{folder.displayName}</span>
            {folder.unreadItemCount > 0 ? (
              <span className="sidebar-link-badge sidebar-folder-row-badge">
                {folder.unreadItemCount > 999 ? "999+" : folder.unreadItemCount}
              </span>
            ) : null}
          </>
        ) : null}
      </Link>
      {!collapsed ? (
        <FavoriteStar
          folderKey={folder.wellKnownName ?? folder.id}
          isFavorite={favorites.has(folder.wellKnownName ?? folder.id)}
          onToggle={onToggleFavorite}
        />
      ) : null}
    </div>
  );
}

interface SearchFolderRowProps {
  folder: SearchFolderSummary;
  onDelete: () => void;
}

function SearchFolderRow({ folder, onDelete }: SearchFolderRowProps) {
  const location = useLocation();
  const to = `/email/folders/${folder.id}`;
  const isActive = sidebarItemIsActive(location.pathname, to);
  return (
    <div className={`sidebar-link sidebar-folder-row${isActive ? " is-active" : ""}`}>
      <span className="sidebar-folder-row-toggle-spacer" aria-hidden />
      <Link to={to} className="sidebar-folder-row-link" title={folder.filterQuery}>
        <span className="sidebar-link-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" {...iconProps}>
            <circle cx="11" cy="11" r="7" />
            <path d="M21 21l-4.35-4.35" />
          </svg>
        </span>
        <span className="sidebar-link-label">{folder.displayName}</span>
        {folder.unreadItemCount > 0 ? (
          <span className="sidebar-link-badge sidebar-folder-row-badge">
            {folder.unreadItemCount > 999 ? "999+" : folder.unreadItemCount}
          </span>
        ) : null}
      </Link>
      <button
        type="button"
        className="sidebar-folder-row-star sidebar-search-folder-delete"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onDelete();
        }}
        aria-label={`Delete search folder ${folder.displayName}`}
        title="Delete search folder"
      >
        ×
      </button>
    </div>
  );
}

interface FavoriteStarProps {
  folderKey: string;
  isFavorite: boolean;
  onToggle: (key: string) => void;
}

function FavoriteStar({ folderKey, isFavorite, onToggle }: FavoriteStarProps) {
  return (
    <button
      type="button"
      className={`sidebar-folder-row-star${isFavorite ? " is-starred" : ""}`}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onToggle(folderKey);
      }}
      aria-label={isFavorite ? "Remove from Favorites" : "Add to Favorites"}
      title={isFavorite ? "Remove from Favorites" : "Add to Favorites"}
    >
      <Star size={16} strokeWidth={1.5} fill={isFavorite ? "currentColor" : "none"} aria-hidden />
    </button>
  );
}

const iconProps = {
  width: 18,
  height: 18,
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.5,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true,
};

// Folder expand chevron — matches the visual style of the existing
// section ChevronIcon in Sidebar.tsx so folder expand toggles read
// the same way as section expand toggles. Rotates -90deg via CSS
// when the wrapping button has the .is-closed class (mirrors the
// section-collapse rule '.sidebar-section.is-closed .sb-chev').
function FolderChevronIcon() {
  return (
    <svg
      className="sb-chev"
      xmlns="http://www.w3.org/2000/svg"
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

// Folder icons — clean Lucide-derived shapes selected for visual
// distinctiveness at 18px. Each icon reads as a different silhouette
// so the user can spot folders at a glance (e.g. Inbox vs Sent vs
// Drafts vs Deleted) without reading the label.
function FolderIcon({ name }: { name: IconName }) {
  switch (name) {
    case "mail":
      // Inbox — Lucide 'inbox' (tray with mail flap).
      return (
        <svg viewBox="0 0 24 24" {...iconProps}>
          <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" />
          <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
        </svg>
      );
    case "send":
      // Sent — Lucide 'send-horizontal' (arrow shooting right).
      return (
        <svg viewBox="0 0 24 24" {...iconProps}>
          <path d="m3 3 3 9-3 9 19-9Z" />
          <path d="M6 12h16" />
        </svg>
      );
    case "draft":
      // Drafts — Lucide 'file-pen' (document with pencil tip).
      return (
        <svg viewBox="0 0 24 24" {...iconProps}>
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h7" />
          <path d="M14 2v6h6" />
          <path d="M21 13.5 16 18.5 13 19l.5-3 5-5a1.4 1.4 0 0 1 2 0z" />
        </svg>
      );
    case "trash":
      // Deleted — Lucide 'trash-2'.
      return (
        <svg viewBox="0 0 24 24" {...iconProps}>
          <path d="M3 6h18" />
          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
          <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
          <line x1="10" y1="11" x2="10" y2="17" />
          <line x1="14" y1="11" x2="14" y2="17" />
        </svg>
      );
    case "archive":
      // Archive — Lucide 'archive'.
      return (
        <svg viewBox="0 0 24 24" {...iconProps}>
          <rect x="2" y="3" width="20" height="5" rx="1" />
          <path d="M4 8v11a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8" />
          <path d="M10 12h4" />
        </svg>
      );
    case "junk":
      // Junk — Lucide 'octagon-alert' (warning octagon).
      return (
        <svg viewBox="0 0 24 24" {...iconProps}>
          <path d="M7.86 2h8.28L22 7.86v8.28L16.14 22H7.86L2 16.14V7.86z" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
      );
    case "outbox":
      // Outbox — Lucide 'send-horizontal' minus arrow + a tray.
      // Use 'inbox' shape but with arrow pointing UP to signal pending
      // outbound delivery (distinct from Inbox's downward flap).
      return (
        <svg viewBox="0 0 24 24" {...iconProps}>
          <polyline points="2 12 8 12 10 9 14 9 16 12 22 12" />
          <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
        </svg>
      );
    case "chat":
      // Conversation History — Lucide 'message-square'.
      return (
        <svg viewBox="0 0 24 24" {...iconProps}>
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
      );
    case "folder":
    default:
      return (
        <svg viewBox="0 0 24 24" {...iconProps}>
          <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
        </svg>
      );
  }
}
